import { v4 as uuidv4 } from "uuid";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../../../schema/types/base";
import { selectContext } from "../../../../store/contextSlice";
import { store } from "../../../../store";
import { FirewallInput, Mk8sInput } from "../../../../schema/types/mk8s/input/mk8s";
import { AwsAmiInput, AwsPoolInput, AwsProviderInput } from "../../../../schema/types/mk8s/input/aws";
import {
  AutoscalerConfigInput,
  NetworkingConfigInput,
  NonCustomizableAddonInput,
  UnmanagedPoolInput,
} from "../../../../schema/types/mk8s/input/common";
import { AzureAmiInput, AzurePoolInput, AzureProviderInput } from "../../../../schema/types/mk8s/input/azure";
import { GenericProviderInput } from "../../../../schema/types/mk8s/input/generic";
import { AddonLogsInput } from "../../../../schema/types/mk8s/input/addons/logs";
import { EphemeralPoolInput, EphemeralProviderInput } from "../../../../schema/types/mk8s/input/ephemeral";
import { AddonAwsEcrInput } from "../../../../schema/types/mk8s/input/addons/awsEcr";
import { AddonAwsEfsInput } from "../../../../schema/types/mk8s/input/addons/awsEfs";
import { AddonAwsElbInput } from "../../../../schema/types/mk8s/input/addons/awsElb";
import { AddonAzureAcrInput } from "../../../../schema/types/mk8s/input/addons/azureAcr";
import { AddonAzureWorkloadIdentityInput } from "../../../../schema/types/mk8s/input/addons/azureWorkloadIdentity";
import { AddonByokInput } from "../../../../schema/types/mk8s/input/addons/byok";
import { getDefaultByokAddonConfig } from "./byokAddonDefaultConfig";
import { AddonMetricsInput } from "../../../../schema/types/mk8s/input/addons/metrics";
import { AddonNvidiaInput } from "../../../../schema/types/mk8s/input/addons/nvidia";
import { AddonRegistryMirrorInput } from "../../../../schema/types/mk8s/input/addons/registryMirror";
import { LinodePoolInput, LinodeProviderInput } from "../../../../schema/types/mk8s/input/linode";
import { OblivusPoolInput, OblivusProviderInput } from "../../../../schema/types/mk8s/input/oblivus";
import {
  TritonPoolInput,
  TritonProviderInput,
  TritonProviderLoadBalancerInput,
  TritonProviderSdcConnectionInput,
} from "../../../../schema/types/mk8s/input/triton";
import { LambdalabsPoolInput, LambdalabsProviderInput } from "../../../../schema/types/mk8s/input/lambdalabs";
import { PaperspacePoolInput, PaperspaceProviderInput } from "../../../../schema/types/mk8s/input/paperspace";
import { DigitalOceanPoolInput, DigitalOceanProviderInput } from "../../../../schema/types/mk8s/input/digitalocean";
import { HetznerPoolInput, HetznerProviderInput } from "../../../../schema/types/mk8s/input/hetzner";
import jsYaml from "js-yaml";
import { Mk8sCreate } from "./inputToMk8s";
import { getDefaultKindSelectState } from "../../../../components/antd/kindselect/KindSelect";
import { GcpImageInput, GcpPoolInput, GcpProviderInput } from "../../../../schema/types/mk8s/input/gcp";

export function getDefaultNetworkingInput(): NetworkingConfigInput {
  let res: NetworkingConfigInput = {
    serviceNetwork: "",
    podNetwork: "",
    dnsForwarder: "",
  };
  return res;
}

export function getDefaultAutoscalerInput(): AutoscalerConfigInput {
  let res: AutoscalerConfigInput = {
    expander: ["most-pods"],
    unneededTime: "",
    unreadyTime: "",
    utilizationThreshold: "",
  };
  return res;
}

export function getDefaultAwsNodePoolInput(): AwsPoolInput {
  let res: AwsPoolInput = {
    id: uuidv4(),
    name: "",
    labels: [],
    taints: [],
    instanceTypes: [],
    overrideImage: { ...getDefaultAwsAmiInput(), type: "none" },
    bootDiskSize: "20",
    maxSize: "1",
    minSize: "1",
    onDemandBaseCapacity: "0",
    onDemandPercentageAboveBaseCapacity: "0",
    spotAllocationStrategy: "lowest-price",
    subnetIds: [],
    extraSecurityGroupIds: [],
    cpuOptions: { nestedVirtualization: false },
  };
  return res;
}

export function getDefaultAwsAmiInput(): AwsAmiInput {
  let res: AwsAmiInput = {
    type: "recommended",
    exact: "",
    recommended: "",
  };
  return res;
}

export function getDefaultAwsInput(): AwsProviderInput {
  let res: AwsProviderInput = {
    autoscaler: getDefaultAutoscalerInput(),
    awsTags: [],
    deployRoleArn: "",
    deployRoleChain: [],
    diskEncryptionKeyArn: "",
    extraNodePolicies: [],
    image: getDefaultAwsAmiInput(),
    keyPair: "",
    networking: getDefaultNetworkingInput(),
    nodePools: [],
    preInstallScript: "",
    region: "",
    securityGroupIds: [],
    skipCreateRoles: false,
    vpcId: "",
  };
  return res;
}

export function getDefaultAzureAmiInput(): AzureAmiInput {
  let res: AzureAmiInput = {
    type: "recommended",
    recommended: "",
    reference: {
      publisher: "",
      offer: "",
      sku: "",
      version: "",
    },
  };
  return res;
}

export function getDefaultAzureNodePoolInput(): AzurePoolInput {
  let res: AzurePoolInput = {
    id: uuidv4(),
    name: "",
    labels: [],
    taints: [],
    size: "",
    subnetId: "",
    zones: [],
    minSize: "1",
    maxSize: "1",
    overrideImage: { ...getDefaultAzureAmiInput(), type: "none" },
    bootDiskSize: "30",
  };
  return res;
}

export function getDefaultAzureInput(): AzureProviderInput {
  let res: AzureProviderInput = {
    location: "",
    subscriptionId: "",
    sdkSecretLink: getDefaultKindSelectState(),
    resourceGroup: "",
    networking: getDefaultNetworkingInput(),
    preInstallScript: "",
    image: getDefaultAzureAmiInput(),
    sshKeys: [],
    networkId: "",
    tags: [],
    nodePools: [],
    autoscaler: getDefaultAutoscalerInput(),
  };
  return res;
}

export function getDefaultGcpNodePoolInput(): GcpPoolInput {
  let res: GcpPoolInput = {
    id: uuidv4(),
    name: "",
    labels: [],
    taints: [],
    machineType: "",
    assignPublicIP: false,
    preemptible: false,
    zone: "",
    overrideImage: { ...getDefaultGcpImageInput(), type: "none" },
    bootDiskSize: "20",
    minSize: "1",
    maxSize: "1",
    subnet: "",
    localPersistentDisks: "",
  };
  return res;
}

export function getDefaultGcpImageInput(): GcpImageInput {
  let res: GcpImageInput = {
    type: "recommended",
    exact: "",
    recommended: "",
    family: {
      project: "",
      family: "",
    },
  };
  return res;
}

export function getDefaultGcpInput(): GcpProviderInput {
  let res: GcpProviderInput = {
    projectId: "",
    region: "",
    labels: [],
    tags: [],
    metadata: [],
    network: "",
    saKeyLink: getDefaultKindSelectState(),
    image: getDefaultGcpImageInput(),
    nodePools: [],
    networking: getDefaultNetworkingInput(),
    preInstallScript: "",
    autoscaler: getDefaultAutoscalerInput(),
  };
  return res;
}

export function getDefaultUnmanagedNodePoolInput(): UnmanagedPoolInput {
  let res: UnmanagedPoolInput = {
    id: uuidv4(),
    labels: [],
    name: "",
    taints: [],
  };
  return res;
}

export function getDefaultGenericInput(): GenericProviderInput {
  let res: GenericProviderInput = {
    location: "",
    networking: getDefaultNetworkingInput(),
    nodePools: [],
  };
  return res;
}

export function getDefaultEphemeralNodePoolInput(): EphemeralPoolInput {
  let res: EphemeralPoolInput = {
    id: uuidv4(),
    name: "",
    arch: "",
    count: "",
    cpu: "",
    flavor: "",
    memory: "",
    labels: [],
    taints: [],
  };
  return res;
}

export function getDefaultEphemeralInput(): EphemeralProviderInput {
  let res: EphemeralProviderInput = {
    location: "",
    nodePools: [],
  };
  return res;
}

export function getDefaultLinodeNodePoolInput(): LinodePoolInput {
  let res: LinodePoolInput = {
    id: uuidv4(),
    name: "",
    labels: [],
    taints: [],
    serverType: "",
    overrideImage: "",
    minSize: "1",
    maxSize: "1",
    subnetId: "",
  };
  return res;
}

export function getDefaultLinodeInput(): LinodeProviderInput {
  let res: LinodeProviderInput = {
    region: "",
    tokenSecretLink: getDefaultKindSelectState(),
    firewallId: "",
    image: "",
    authorizedUsers: [],
    authorizedKeys: [],
    vpcId: "",
    nodePools: [],
    networking: getDefaultNetworkingInput(),
    preInstallScript: "",
    autoscaler: getDefaultAutoscalerInput(),
  };
  return res;
}

export function getDefaultOblivusNodePoolInput(): OblivusPoolInput {
  let res: OblivusPoolInput = {
    id: uuidv4(),
    name: "",
    labels: [],
    taints: [],
    minSize: "1",
    maxSize: "1",
    flavor: "",
  };
  return res;
}

export function getDefaultOblivusInput(): OblivusProviderInput {
  let res: OblivusProviderInput = {
    datacenter: "",
    tokenSecretLink: getDefaultKindSelectState(),
    nodePools: [],
    sshKeys: [],
    unmanagedNodePools: [],
    autoscaler: getDefaultAutoscalerInput(),
    preInstallScript: "",
  };
  return res;
}

export function getDefaultTritonNodePoolInput(): TritonPoolInput {
  let res: TritonPoolInput = {
    id: "",
    name: "",
    labels: [],
    taints: [],
    packageId: "",
    overrideImageId: "",
    publicNetworkId: "",
    privateNetworkIds: [],
    tritonTags: [],
    minSize: "1",
    maxSize: "1",
  };
  return res;
}

export function getDefaultTritonLoadBalancerInput(): TritonProviderLoadBalancerInput {
  let res: TritonProviderLoadBalancerInput = {
    type: "gateway",
    manual: {
      packageId: "",
      imageId: "",
      publicNetworkId: "",
      privateNetworkIds: [],
      metadata: [],
      tags: [],
      logging: {
        nodePort: "",
        externalSyslog: "",
      },
      count: "",
      cnsInternalDomain: "",
      cnsPublicDomain: "",
    },
  };
  return res;
}

export function getDefaultTritonConnectionInput(): TritonProviderSdcConnectionInput {
  let res: TritonProviderSdcConnectionInput = {
    url: "",
    account: "",
    user: "",
    privateKeySecretLink: getDefaultKindSelectState(),
  };
  return res;
}

export function getDefaultTritonInput(): TritonProviderInput {
  let res: TritonProviderInput = {
    connection: getDefaultTritonConnectionInput(),
    location: "",
    privateNetworkId: "",
    firewallEnabled: false,
    loadBalancer: getDefaultTritonLoadBalancerInput(),
    nodePools: [],
    imageId: "",
    sshKeys: [],
    autoscaler: getDefaultAutoscalerInput(),
    networking: getDefaultNetworkingInput(),
    preInstallScript: "",
  };
  return res;
}

export function getDefaultLambdalabsNodePoolInput(): LambdalabsPoolInput {
  let res: LambdalabsPoolInput = {
    id: "",
    name: "",
    labels: [],
    taints: [],
    minSize: "1",
    maxSize: "1",
    instanceType: "",
  };
  return res;
}

export function getDefaultLambdalabsInput(): LambdalabsProviderInput {
  let res: LambdalabsProviderInput = {
    region: "",
    tokenSecretLink: getDefaultKindSelectState(),
    nodePools: [],
    sshKey: "",
    unmanagedNodePools: [],
    preInstallScript: "",
    autoscaler: getDefaultAutoscalerInput(),
    fileSystems: [],
  };
  return res;
}

export function getDefaultPaperspaceNodePoolInput(): PaperspacePoolInput {
  let res: PaperspacePoolInput = {
    id: "",
    name: "",
    publicIpType: "none",
    labels: [],
    taints: [],
    minSize: "1",
    maxSize: "1",
    bootDiskSize: "50",
    machineType: "",
  };
  return res;
}

export function getDefaultPaperspaceInput(): PaperspaceProviderInput {
  let res: PaperspaceProviderInput = {
    region: "",
    tokenSecretLink: getDefaultKindSelectState(),
    sharedDrives: [],
    nodePools: [],
    unmanagedNodePools: [],
    autoscaler: getDefaultAutoscalerInput(),
    preInstallScript: "",
    userIds: [],
    networkId: "",
  };
  return res;
}

export function getDefaultDigitalOceanNodePoolInput(): DigitalOceanPoolInput {
  let res: DigitalOceanPoolInput = {
    id: "",
    name: "",
    labels: [],
    taints: [],
    dropletSize: "",
    overrideImage: "",
    minSize: "1",
    maxSize: "1",
  };
  return res;
}

export function getDefaultDigitalOceanInput(): DigitalOceanProviderInput {
  let res: DigitalOceanProviderInput = {
    region: "",
    digitalOceanTags: [],
    tokenSecretLink: getDefaultKindSelectState(),
    vpcId: "",
    nodePools: [],
    image: "",
    sshKeys: [],
    extraSshKeys: [],
    reservedIPs: [],
    autoscaler: getDefaultAutoscalerInput(),
    networking: getDefaultNetworkingInput(),
    preInstallScript: "",
  };
  return res;
}

export function getDefaultHetznerNodePoolInput(): HetznerPoolInput {
  let res: HetznerPoolInput = {
    id: "",
    name: "",
    labels: [],
    taints: [],
    serverType: "",
    overrideImage: "",
    minSize: "1",
    maxSize: "1",
  };
  return res;
}

export function getDefaultHetznerInput(): HetznerProviderInput {
  let res: HetznerProviderInput = {
    region: "",
    hetznerLabels: [],
    networking: getDefaultNetworkingInput(),
    preInstallScript: "",
    tokenSecretLink: getDefaultKindSelectState(),
    networkId: "",
    firewallId: "",
    nodePools: [],
    dedicatedServerNodePools: [],
    image: "",
    sshKey: "",
    autoscaler: getDefaultAutoscalerInput(),
    floatingIPSelector: [],
  };
  return res;
}

export function getDefaultNonCustomizableAddonInput(): NonCustomizableAddonInput {
  return {
    isEnabled: false,
    value: "",
  };
}

export function getDefaultLogsAddonInput(): AddonLogsInput {
  return {
    isEnabled: false,
    auditEnabled: false,
    docker: false,
    events: false,
    excludeNamespaces: "",
    includeNamespaces: "",
    kernel: false,
    kubelet: false,
  };
}

export function getDefaultAwsEcrAddonInput(): AddonAwsEcrInput {
  return {
    isEnabled: false,
    roleArn: "",
  };
}
export function getDefaultAwsEfsAddonInput(): AddonAwsEfsInput {
  return {
    isEnabled: false,
    roleArn: "",
  };
}
export function getDefaultAwsElbAddonInput(): AddonAwsElbInput {
  return {
    isEnabled: false,
    roleArn: "",
  };
}

export function getDefaultAzureAcrAddonInput(): AddonAzureAcrInput {
  return {
    isEnabled: false,
    clientId: "",
  };
}
export function getDefaultAzureWorkloadIdentityAddonInput(): AddonAzureWorkloadIdentityInput {
  return {
    isEnabled: false,
    tenantId: "",
  };
}
export function getDefaultByokAddonInput(): AddonByokInput {
  const configObject = getDefaultByokAddonConfig();
  const configText = jsYaml.dump(configObject);

  return {
    isEnabled: false,
    ignoreUpdates: false,
    location: getDefaultKindSelectState(),
    config: configText,
  };
}

export function getDefaultMetricsAddonInput(): AddonMetricsInput {
  return {
    isEnabled: false,
    apiserver: false,
    cadvisor: false,
    coreDns: false,
    kubelet: false,
    kubeState: false,
    nodeExporter: false,
    scrapeAnnotated: {
      isEnabled: false,
      excludeNamespaces: "",
      includeNamespaces: "",
      intervalSeconds: "",
      retainLabels: "",
    },
  };
}
export function getDefaultNvidiaAddonInput(): AddonNvidiaInput {
  return {
    isEnabled: false,
    taintGPUNodes: false,
  };
}
export function getDefaultRegistryMirrorAddonInput(): AddonRegistryMirrorInput {
  return {
    isEnabled: false,
    mirrors: [],
  };
}

export function getDefaultFirewallInput(): FirewallInput[] {
  return [{ id: uuidv4(), sourceCIDR: "0.0.0.0/0", description: "Default allow-all rule" }];
}

export function getDefaultMk8sInput(): Mk8sInput {
  const context = selectContext(store.getState());

  return {
    ...getDefaultBaseInput(),
    version: 0,
    id: "",
    specVersion: "1.35.3",
    provider: "generic",
    aws: getDefaultAwsInput(),
    azure: getDefaultAzureInput(),
    digitalocean: getDefaultDigitalOceanInput(),
    ephemeral: getDefaultEphemeralInput(),
    gcp: getDefaultGcpInput(),
    generic: getDefaultGenericInput(),
    hetzner: getDefaultHetznerInput(),
    lambdalabs: getDefaultLambdalabsInput(),
    linode: getDefaultLinodeInput(),
    oblivus: getDefaultOblivusInput(),
    paperspace: getDefaultPaperspaceInput(),
    triton: getDefaultTritonInput(),
    firewall: getDefaultFirewallInput(),
    tags: [],
    addons: {
      awsECR: getDefaultAwsEcrAddonInput(),
      awsEFS: getDefaultAwsEfsAddonInput(),
      awsELB: getDefaultAwsElbAddonInput(),
      awsWorkloadIdentity: getDefaultNonCustomizableAddonInput(),
      azureACR: getDefaultAzureAcrAddonInput(),
      azureWorkloadIdentity: getDefaultAzureWorkloadIdentityAddonInput(),
      byok: getDefaultByokAddonInput(),
      localPathStorage: getDefaultNonCustomizableAddonInput(),
      metrics: getDefaultMetricsAddonInput(),
      nvidia: getDefaultNvidiaAddonInput(),
      registryMirror: getDefaultRegistryMirrorAddonInput(),
      sysbox: getDefaultNonCustomizableAddonInput(),
      dashboard: getDefaultNonCustomizableAddonInput(),
      headlamp: getDefaultNonCustomizableAddonInput(),
      logs: getDefaultLogsAddonInput(),
    },
    meta: {
      validationCallId: "",
      clusterLink: "",
      aws: {
        activeKeyNodePool: "",
        accountId: "",
      },
      azure: {
        activeKeyNodePool: "",
        subscription: "",
      },
      digitalocean: {
        activeKeyNodePool: "",
      },
      ephemeral: {
        activeKeyNodePool: "",
      },
      gcp: {
        activeKeyNodePool: "",
      },
      generic: {
        activeKeyNodePool: "",
      },
      hetzner: {
        activeKeyNodePool: "",
        activeKeyDedicatedServerNodePool: "",
      },
      lambdalabs: {
        activeKeyNodePool: "",
        activeKeyUnmanagedNodePool: "",
      },
      linode: {
        activeKeyNodePool: "",
      },
      oblivus: {
        activeKeyNodePool: "",
        activeKeyUnmanagedNodePool: "",
      },
      paperspace: {
        activeKeyNodePool: "",
        activeKeyUnmanagedNodePool: "",
      },
      triton: {
        activeKeyNodePool: "",
      },
      isCreate: true,
    },
  };
}

export function mk8sToPatch(_mk8s: Mk8sCreate, concurrency: OptimisticConcurrency) {
  const mk8s: any = JSON.parse(JSON.stringify(_mk8s));

  mk8s[`$replace/spec`] = mk8s.spec;
  delete mk8s.spec;

  mk8s[`$replace/tags`] = mk8s.tags;
  delete mk8s.tags;

  mk8s.id = concurrency.id;
  mk8s.version = concurrency.version;

  return mk8s;
}
